import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StepProps } from "@/lib/builder/models/builder.model";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { ItineraryTotals } from "./models/itinerary.model";
import { ItineraryBuilder } from "./models/itinerary-builder.model";
import { Calendar, MapPin, Plane, Car, Utensils, Ticket, StickyNote, Home, CarTaxiFront, FerrisWheel, CupSoda, MicVocal, FileText, Bed } from "lucide-react";
import { ItineraryActivity } from "./models/activity.model";

const CheckoutStep = ({
    data,
    onDataChange,
    builder
}: StepProps) => {
    const [updatedTotals, setUpdatedTotals] = useState<ItineraryTotals>({
        commisionable_total: 0,
        not_commisionable_total: 0,
        p2b_commission_percentage: 22,
        p2b_commission_amount: 0,
        td_fee_percentage: 70,
        total: 0,
    });

    // Inizializza builder se non passato
    const itineraryBuilder = useMemo(() => {
        if (builder) return builder;

        if (data?.start_date && data?.end_date && data?.itinerary_days) {
            const newBuilder = new ItineraryBuilder();
            newBuilder.loadFromArray(data.itinerary_days, data.start_date, data.end_date);
            return newBuilder;
        }
        return null;
    }, [builder, data]);

    useEffect(() => {
        setUpdatedTotals(data.extractTotals());
    }, [data]);

    // Helper function to safely access cost property
    const getActivityCost = (activity: ItineraryActivity): number => {
        return (activity as { cost?: number }).cost || 0;
    };

    // Helper function to check if activity should be charged
    const isChargeable = (activity: ItineraryActivity): boolean => {
        return activity.include_price && getActivityCost(activity) > 0;
    };

    // Helper function to check if activity should be shown in checkout
    const shouldShowInCheckout = (activity: ItineraryActivity): boolean => {
        // Escludi attività OUT (check-out)
        if (activity.type === 'accommodation' && (activity as any).inOrOutActivity === 'out') {
            return false;
        }
        return getActivityCost(activity) > 0;
    };

    // Format date for display
    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    // Get activity icon and color based on type
    const getActivityIcon = (activity: ItineraryActivity) => {
        const type = activity.type;
        
        switch (type) {
            case 'accommodation':
                return { icon: Bed, color: 'text-gray-900' };
            case 'flight':
                return { icon: Plane, color: 'text-gray-900' };
            case 'transportation':
                return { icon: CarTaxiFront, color: 'text-gray-900' };
            case 'activity':
                return { icon: FerrisWheel, color: 'text-gray-900' };
            case 'food-drinks':
                return { icon: CupSoda, color: 'text-gray-900' };
            case 'events':
                return { icon: MicVocal, color: 'text-gray-900' };
            case 'notes':
                return { icon: FileText, color: 'text-gray-900' };
            default:
                return { icon: MapPin, color: 'text-gray-900' };
        }
    };

    return (
        <div className="w-full h-full mx-auto p-5 overflow-hidden flex justify-center">
            <div className="flex flex-col sm:flex-row gap-6 w-full md:w-full lg:w-[90%] xl:w-[80%] h-full sm:items-center">
                {/* Left Card - Activities Breakdown */}
                <Card className="flex flex-col w-full sm:w-[50%] md:w-[60%] lg:w-[70%] h-full max-h-full overflow-hidden">
                    {/* <CardHeader className="flex-shrink-0">
                        <CardTitle className="text-lg font-semibold text-black">Activities Breakdown</CardTitle>
                    </CardHeader> */}

                    <CardContent className="flex-1 pt-4 overflow-y-auto min-h-0">
                        <div className="space-y-6 pr-2">
                            {itineraryBuilder?.getAllDays().map((day) => {
                                const dayActivities = day.activities.filter(a => shouldShowInCheckout(a));

                                if (dayActivities.length === 0) return null;

                                return (
                                    <div key={day.date} className="space-y-3">
                                        {/* Day Header */}
                                        <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                                            <Calendar className="w-4 h-4 text-gray-500" />
                                            <span className="text-sm font-semibold text-gray-900">
                                                {formatDate(day.date)}
                                            </span>
                                        </div>

                                        {/* Activities for this day */}
                                        <div className="space-y-2 pl-6">
                                            {dayActivities.map((activity) => {
                                                const chargeable = isChargeable(activity);
                                                const { icon: Icon, color } = getActivityIcon(activity);

                                                return (
                                                    <div
                                                        key={activity.id}
                                                        className={`flex items-start justify-between ${!chargeable ? 'opacity-40' : ''}`}
                                                    >
                                                        <div className="flex items-start gap-2 flex-1">
                                                            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${chargeable ? color : 'text-gray-400'}`} />
                                                            <div className="flex-1">
                                                                <span className={`text-sm ${chargeable ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                                                                    {activity.title}
                                                                </span>
                                                                {!chargeable && (
                                                                    <span className="ml-2 text-xs text-gray-400 italic">
                                                                        (not charged)
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className={`text-sm ${chargeable ? 'text-gray-900 font-semibold' : 'text-gray-400'} ml-4 flex-shrink-0`}>
                                                            €{getActivityCost(activity).toLocaleString()}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* Right Card - Revenue Breakdown */}
                <Card className="flex flex-col w-full sm:w-[50%] md:w-[40%] lg:w-[30%] h-auto max-h-full">
                    {/* <CardHeader className="flex-shrink-0">
                        <CardTitle className="text-lg font-semibold text-black">Revenue Breakdown</CardTitle>
                    </CardHeader> */}

                    <CardContent className="p-6 flex flex-col flex-shrink-0">
                        <div className="space-y-6">
                            {/* Commissionable Total */}
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-black">Commissionable total</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        €{updatedTotals?.commisionable_total?.toLocaleString() ?? '-'}
                                    </span>
                                </div>
                            </div>

                            {/* Separator */}
                            <div className="border-t border-gray-300"></div>

                            {/* Total Commission */}
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-black">Total commission</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        €{updatedTotals?.p2b_commission_amount?.toLocaleString() ?? '-'}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500">
                                    ({updatedTotals?.p2b_commission_percentage ?? '20'}% of Commissionable total)
                                </div>
                            </div>

                            {/* Your Earnings */}
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-black">Your Earnings</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        €{((updatedTotals.td_fee_percentage / 100) * updatedTotals.p2b_commission_amount)?.toLocaleString() ?? '-'}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500">
                                    ({updatedTotals?.td_fee_percentage ?? '70'}% of Total commission)
                                </div>
                            </div>
                        </div>

                        {/* Total Section - Bottom */}
                        <div className="space-y-4 pt-6">
                            {/* Separator */}
                            <div className="border-t-2 border-gray-400"></div>

                            {/* Total (with fee) */}
                            <div className="flex items-center justify-between">
                                <span className="text-lg font-semibold text-black">Total</span>
                                <span className="text-lg font-semibold text-gray-900">
                                    €{(updatedTotals?.total)?.toLocaleString() ?? '-'}
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default CheckoutStep;
